import { useLocation, useNavigate } from "react-router-dom";
import { Drawer, Hidden, List, ListItemButton, ListItemText } from "@mui/material";
import { alpha, lighten, Theme, useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import AccountsIcon from "@mui/icons-material/ContactsSharp";
import PeopleIcon from "@mui/icons-material/People";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useLogo } from "../../../brand/hooks";
import { parseFirstName, parseLastName } from "../../../users/utils";
import UITypography from "../ui/UITypography";
import { useAuthSession } from "../../../auth/hooks";
import { usePartnerAdminStatus } from "../../../partner-users/hooks";
import { useMonitoringAccess } from "../../../monitoring/hooks";

interface MainProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
  hasWelcome?: boolean;
}
export const drawerWidth = 264;

function SideMenu({ mobileOpen, handleDrawerToggle, hasWelcome = false }: MainProps) {
  const classes = useStyles({ drawerWidth });
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: session } = useAuthSession();
  const { data: adminStatus } = usePartnerAdminStatus();
  const { data: monitoringAccess } = useMonitoringAccess();
  const logo = useLogo();

  // Define page arrays for active state detection
  const accountPages = ["/", "/accounts"];
  const userPages = ["/partner-users", "/users"];
  const monitoringPages = ["/monitoring"];

  // Active page detection function
  const isPageActive = (pages: string[]): boolean => {
    return pages.some((p) => {
      if (p === "/") return location.pathname === p;
      else return location.pathname.startsWith(p);
    });
  };

  // Helper function for getting class names with active state
  const getClassNames = (pages: string[]) => `${classes.listItem} ${isPageActive(pages) ? classes.itemActive : ""}`;

  const canView: Function = (type: string) => {
    switch (type) {
      case "accounts":
        return true;
      case "users":
        return adminStatus?.isAdmin === true;
      case "monitoring":
        return monitoringAccess?.hasAccess === true;
      default:
        return false;
    }
  };

  const DrawerList = () => {
    return (
      <div role="presentation" className={classes.listWrap}>
        <div className={classes.logo}>
          <div
            data-testid="nav-drawer-logo"
            style={{
              width: "200px",
              height: "110px",
              background: `url('${logo}') center / contain no-repeat`,
            }}
          />
        </div>
        {hasWelcome && (
          <UITypography className={classes.welcome} variant="subtitle2">
            Welcome
            {parseFirstName(session?.userProfile) ? ", " + parseLastName(session?.userProfile) : ""}
          </UITypography>
        )}
        <List disablePadding={false} className={classes.links}>
          {canView("accounts") && (
            <ListItemButton className={getClassNames(accountPages)} data-testid="nav-drawer-item-home" onClick={() => navigate("/")}>
              <AccountsIcon className={classes.icon} />
              <ListItemText primary="Accounts" classes={{ primary: classes.itemText }} />
            </ListItemButton>
          )}
          {canView("users") && (
            <ListItemButton className={getClassNames(userPages)} data-testid="nav-drawer-item-users" onClick={() => navigate("/partner-users")}>
              <PeopleIcon className={classes.icon} />
              <ListItemText primary="Partner Users" classes={{ primary: classes.itemText }} />
            </ListItemButton>
          )}
          {canView("monitoring") && (
            <ListItemButton className={getClassNames(monitoringPages)} data-testid="nav-drawer-item-billing-alert-failures" onClick={() => navigate("/monitoring/billing-alert-failures")}>
              <WarningAmberIcon className={classes.icon} />
              <ListItemText primary="Billing Alert Failures" classes={{ primary: classes.itemText }} />
            </ListItemButton>
          )}
        </List>
      </div>
    );
  };

  return (
    <nav className={classes.drawer}>
      <Hidden smUp implementation="css">
        <Drawer
          variant="temporary"
          anchor={theme.direction === "rtl" ? "right" : "left"}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <DrawerList />
        </Drawer>
      </Hidden>
      <Hidden smDown implementation="css">
        <Drawer
          classes={{
            paper: classes.drawerPaper,
          }}
          variant="permanent"
          open
        >
          <DrawerList />
        </Drawer>
      </Hidden>
    </nav>
  );
}

const useStyles = makeStyles<Theme, { drawerWidth: number }>((theme: Theme) =>
  createStyles({
    listWrap: {
      width: drawerWidth,
      backgroundColor: theme.palette.background.paper,
      height: "100%",
    },
    logo: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "16px 24px",
    },
    welcome: {
      padding: "0px 12px 4px 12px",
      textAlign: "center",
    },
    links: {
      padding: "8px 0px",
    },
    listItem: {
      padding: "10px 12px",
      margin: "2px 12px",
      width: "auto",
      borderRadius: 8,
      color: theme.palette.text.primary,
      cursor: "pointer",
      transition: "background-color .15s ease",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
      },
    },
    itemText: {
      fontSize: "14px",
      fontWeight: 500,
    },
    itemActive: {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      "& .MuiListItemText-primary": {
        color: theme.palette.primary.main,
        fontWeight: 600,
      },
    },
    icon: {
      marginRight: "20px",
      width: "20px",
      height: "20px",
      color: "inherit",
      opacity: 0.75,
    },
    divider: {
      backgroundColor: lighten(theme.palette.tertiary.main, 0.2),
    },
    drawer: (props) => ({
      [theme.breakpoints.up("sm")]: {
        width: props.drawerWidth,
        flexShrink: 0,
      },
      color: theme.palette.text.primary,
    }),
    drawerPaper: (props) => ({
      width: props.drawerWidth,
      overflowX: "hidden",
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderRight: `1px solid ${theme.palette.grey[100]}`,
    }),
  })
);

export default SideMenu;
